import { useEffect, useState } from 'react';

import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { getCookieChoice, hasCookieConsent, subscribeCookieConsent } from 'utils/cookieConsent';
import { isProfileAnalyticsHost } from 'utils/metrikaGoals';

function isRybbitHost(): boolean {
  if (isProfileAnalyticsHost()) return true;
  if (typeof window === 'undefined') return false;
  return window.location.hostname.toLowerCase() === 'profile.dev.myteacher.pro';
}

type AnalyticsConfig = {
  enabled: boolean;
  site_id: string;
  script_src: string;
};

type RybbitApi = {
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  clearUserId: () => void;
  stopSessionReplay?: () => void;
};

declare global {
  interface Window {
    rybbit?: RybbitApi;
  }
}

const OPT_OUT_KEY = 'disable-rybbit';

function rybbitAllowed(): boolean {
  return getCookieChoice() !== 'rejected';
}

function syncOptOut(): void {
  try {
    if (getCookieChoice() === 'rejected') localStorage.setItem(OPT_OUT_KEY, '1');
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* private mode */
  }
}

function roleFromIds(roles: number[]): string | undefined {
  if (roles.includes(1) && roles.includes(2)) return 'tutor_student';
  if (roles.includes(1)) return 'tutor';
  if (roles.includes(2)) return 'student';
  return undefined;
}

function waitForRybbit(timeoutMs = 8000): Promise<RybbitApi | null> {
  const started = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      if (window.rybbit?.identify) {
        resolve(window.rybbit);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(null);
        return;
      }
      window.setTimeout(tick, 80);
    };
    tick();
  });
}

function injectScript(src: string, siteId: string, replay: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-rybbit-site]');
    if (existing) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.defer = true;
    el.dataset.siteId = siteId;
    el.dataset.rybbitSite = siteId;
    if (!replay) el.setAttribute('data-replay-sample-rate', '0');
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('analytics script'));
    document.head.appendChild(el);
  });
}

export function RybbitTracker() {
  const { data: session } = useAuthSession();
  const [allowed, setAllowed] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    if (!isRybbitHost()) return;
    const sync = () => {
      syncOptOut();
      setAllowed(rybbitAllowed());
      setConsentGiven(hasCookieConsent());
      if (!rybbitAllowed()) window.rybbit?.stopSessionReplay?.();
    };
    sync();
    return subscribeCookieConsent(sync);
  }, []);

  useEffect(() => {
    if (!allowed || !isRybbitHost()) return;
    let cancelled = false;

    const run = async () => {
      try {
        syncOptOut();
        const response = await fetch('/v1/public/analytics-config/', {
          credentials: 'omit',
        });
        if (!response.ok) return;
        const config = (await response.json()) as AnalyticsConfig;
        if (cancelled || !config.enabled || !config.site_id || !rybbitAllowed()) return;
        await injectScript(
          config.script_src || '/analytics/script.js',
          config.site_id,
          hasCookieConsent(),
        );
      } catch {
        /* tracker is best-effort */
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  useEffect(() => {
    if (!consentGiven || !isRybbitHost()) return;
    let cancelled = false;

    const run = async () => {
      const rybbit = await waitForRybbit();
      if (cancelled || !rybbit) return;
      if (isAuthSessionActive(session) && session?.id) {
        rybbit.identify(String(session.id), {
          role: roleFromIds(session.roles ?? []) || 'user',
        });
      } else if (rybbit.clearUserId) {
        rybbit.clearUserId();
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [consentGiven, session]);

  return null;
}
